/**
 * Calculates the discontinue date based on an effective date.
 * The discontinue date is simply one day before the effective date.
 * 
 * @param effectiveDate Effective date string in YYYY-MM-DD format
 * @returns The calculated discontinue date in ISO string format (YYYY-MM-DD), or empty string if effectiveDate is invalid
 */
export const calculateDiscontinueDate = (effectiveDate: string): string => {
  if (!effectiveDate) {
    return '';
  }

  // Parse the effective date
  const effectiveDateObj = new Date(effectiveDate);
  if (isNaN(effectiveDateObj.getTime())) {
    return '';
  }

  // Subtract one day to get the discontinue date
  const discontinueDate = new Date(effectiveDateObj);
  discontinueDate.setDate(discontinueDate.getDate() - 1);

  // Format as YYYY-MM-DD
  return discontinueDate.toISOString().split('T')[0];
};

/**
 * Calculates the minimum effective date from a list of replacement ranges
 *
 * @param replacementRanges Array of replacement ranges with effectiveDate property
 * @returns The minimum effective date in ISO string format (YYYY-MM-DD), or empty string if no valid dates
 */
export const findMinEffectiveDate = (replacementRanges: Array<{ effectiveDate?: string }>): string => {
  if (!replacementRanges || replacementRanges.length === 0) {
    return '';
  }

  let minDate: Date | null = null;

  // Find the minimum effective date
  for (const range of replacementRanges) {
    if (!range.effectiveDate) continue;

    const effectiveDate = new Date(range.effectiveDate);
    if (isNaN(effectiveDate.getTime())) continue;

    if (!minDate || effectiveDate < minDate) {
      minDate = effectiveDate;
    }
  }

  if (!minDate) {
    return '';
  }

  // Format as YYYY-MM-DD
  return minDate.toISOString().split('T')[0];
};

/**
 * Calculates discontinue dates for multiple selected ranges based on their replacement ranges.
 * For each selected range, finds the minimum effective date among its replacements,
 * and sets the discontinue date to one day before that minimum effective date.
 *
 * @param replacementRangesMap Map of selected range IDs to their replacement ranges
 * @returns Map of selected range IDs to their calculated discontinue dates
 */
export const calculateDiscontinueDates = (
  replacementRangesMap: Record<string, Array<{ effectiveDate?: string }>>
): Record<string, string> => {
  const discontinueDateMap: Record<string, string> = {};

  // For each selected range
  Object.entries(replacementRangesMap).forEach(([rangeId, replacements]) => {
    // Find the minimum effective date among its replacements
    const minEffectiveDate = findMinEffectiveDate(replacements);

    // Calculate discontinue date based on this minimum effective date
    discontinueDateMap[rangeId] = calculateDiscontinueDate(minEffectiveDate);
  });

  return discontinueDateMap;
};

// formatDate function removed as it's not used in the application

/**
 * Validate if a date string is in valid format
 * @param dateString - The date string to validate
 * @returns Boolean indicating if date is valid
 */
export const isValidDate = (dateString: string): boolean => {
  if (!dateString) return false;
  
  // Special handling for YYYY-MM-DD format to avoid timezone issues
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
    
    // Check for valid month and day values
    if (month < 1 || month > 12) return false;
    
    // Check days in month (simplified)
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) return false;
    
    return true;
  }
  
  // For other formats, use standard Date parsing
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Checks if a date string represents a current or future date
 * @param dateString - The date string to validate in YYYY-MM-DD format
 * @returns Boolean indicating if date is current or in the future
 */
export const isCurrentOrFutureDate = (dateString: string): boolean => {
  if (!isValidDate(dateString)) return false;

  // Create date objects with time set to start of day for consistent comparison
  const inputDate = new Date(dateString);
  inputDate.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Date is valid if it's today or in the future
  return inputDate >= today;
};

/**
 * Checks if a date string represents a current or future date
 * @returns String of today in YYYY-MM-DD format
 */
export const getToday = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
  const day = today.getDate().toString().padStart(2, '0');

  const customFormat = `${year}-${month}-${day}`;

  return customFormat;
};

const dateUtils = {
  calculateDiscontinueDate,
  isValidDate,
  findMinEffectiveDate,
  calculateDiscontinueDates,
  isCurrentOrFutureDate,
  getToday
};

export default dateUtils;
